import { useEffect, useState } from "react";
import {
  ExternalLink,
  HelpCircle,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Undo2,
} from "lucide-react";
import type { EventEntry } from "../../../src/fields.ts";
import { type Category, cats } from "../../../src/category.ts";
import { api } from "../api.ts";
import { db } from "../db.ts";
import { id } from "@instantdb/react";
import { Spinner } from "./Spinner.tsx";

const monthNames = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const pad2 = (value: number) => String(value).padStart(2, "0");

const toQueryDatetime = (date: Date) =>
  `${monthNames[date.getMonth()]}-${pad2(date.getDate())} ${
    pad2(date.getHours())
  }:${pad2(date.getMinutes())}`;

const toQueryEndOfDay = (date: Date) =>
  toQueryDatetime(
    new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      0,
      0,
    ),
  );

const isRtlText = (text: string) => /[\u0590-\u05ff\u0600-\u06ff]/.test(text);

const createDefaultQuery = (
  selectedCategory: Category | "",
  selectedLocation: string,
) => {
  const now = new Date();
  const end = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 30,
    now.getHours(),
    now.getMinutes(),
    0,
    0,
  );

  return {
    time: {
      startDatetime: toQueryDatetime(now),
      endDatetime: toQueryEndOfDay(end),
    },
    onlyStartedInRange: true,
    limit: 500,
    ...(selectedCategory ? { categories: [selectedCategory] } : {}),
    ...(selectedLocation.trim() ? { location: selectedLocation.trim() } : {}),
  };
};

const withoutSwipedEvents = (
  events: EventEntry[],
  swipedEventIds: string[],
) => {
  const swiped = new Set(swipedEventIds);
  return events.filter(({ id }) => !swiped.has(id));
};

const recordSwipe = (
  userId: string,
  event: EventEntry,
  direction: "like" | "dislike",
  swipeId: string,
) =>
  db.transact(
    db.tx.swipes[swipeId].create({
      eventId: event.id,
      direction,
      timestamp: Date.now(),
    }).link({ user: userId }),
  );

const EventDetails = ({ event }: { event: EventEntry }) => {
  const eventDate = new Date(event.structured.timestamp.start)
    .toLocaleDateString(
      "en-US",
      { month: "long", day: "numeric", year: "numeric" },
    );

  const isTitleRtl = isRtlText(event.structured.name);
  const isDescRtl = event.structured.description
    ? isRtlText(event.structured.description)
    : false;

  return (
    <>
      <div dir="ltr" className="text-left">
        {event.recommendationScore !== undefined && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-300 border border-purple-500/30">
              ✨ {Math.round(event.recommendationScore * 100)}% Match
            </span>
          </div>
        )}
        <div className="flex items-start gap-2 justify-between">
          <h2
            dir={isTitleRtl ? "rtl" : "ltr"}
            className={`text-2xl font-bold text-white mb-2 leading-snug flex-1 ${
              isTitleRtl ? "text-right" : "text-left"
            }`}
          >
            {event.structured.name}
          </h2>
          {event.structured.links.length > 0 && (
            <a
              href={event.structured.links[0]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 p-1.5 rounded-lg hover:bg-white/5 transition shrink-0"
              title="Event Link"
            >
              <ExternalLink size={20} />
            </a>
          )}
        </div>
        {event.structured.description && (
          <p
            dir={isDescRtl ? "rtl" : "ltr"}
            className={`text-gray-400 text-sm mb-4 leading-relaxed ${
              isDescRtl ? "text-right" : "text-left"
            }`}
          >
            {event.structured.description}
          </p>
        )}
        {event.structured.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {event.structured.categories.slice(0, 4).map((cat) => (
              <span
                key={cat}
                className="text-xs px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 font-medium"
              >
                {cat}
              </span>
            ))}
          </div>
        )}
      </div>

      <div
        dir="ltr"
        className="space-y-2 text-sm text-gray-400 text-left"
      >
        <p>
          <span className="font-semibold">📅 When:</span> {eventDate}
        </p>
        {event.structured.venue && (
          <p>
            <span className="font-semibold">📍 Where:</span>{" "}
            {event.structured.venue}
          </p>
        )}
        {event.structured.location && (
          <p>
            <span className="font-semibold">🗺️ Location:</span>{" "}
            {event.structured.location}
          </p>
        )}
      </div>
    </>
  );
};

interface SwipeButtonsProps {
  loading: boolean;
  currentIndex: number;
  totalEvents: number;
  onSwipe: (direction: "like" | "dislike") => void;
  onSkip: () => void;
  onUndo: () => void;
  canUndo: boolean;
}

const SwipeButtons = (
  {
    loading,
    currentIndex,
    totalEvents,
    onSwipe,
    onSkip,
    onUndo,
    canUndo,
  }: SwipeButtonsProps,
) => (
  <div className="space-y-3 pt-4">
    {/* Primary Actions: Wide, Prominent, Easy to Tap */}
    <div className="flex gap-3">
      <button
        type="button"
        onClick={() => onSwipe("dislike")}
        disabled={loading}
        className="flex-1 bg-gradient-to-r from-red-500/30 to-red-600/30 hover:from-red-500/50 hover:to-red-600/50 border border-red-500/50 text-red-200 font-semibold py-3 rounded-lg transition transform hover:scale-105 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <ThumbsDown size={18} /> Pass
      </button>
      <button
        type="button"
        onClick={() => onSwipe("like")}
        disabled={loading}
        className="flex-1 bg-gradient-to-r from-green-500/30 to-emerald-600/30 hover:from-green-500/50 hover:to-emerald-600/50 border border-green-500/50 text-green-200 font-semibold py-3 rounded-lg transition transform hover:scale-105 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <ThumbsUp size={18} /> Like
      </button>
    </div>

    {/* Secondary Actions: Utility Row */}
    <div className="flex gap-3">
      <button
        type="button"
        onClick={onUndo}
        disabled={loading || !canUndo}
        className="flex-1 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-lg transition transform hover:scale-105 active:scale-95 disabled:opacity-30 flex items-center justify-center gap-1.5 text-xs font-medium"
        title="Undo last action"
      >
        <Undo2 size={14} /> Undo
      </button>
      <button
        type="button"
        onClick={onSkip}
        disabled={loading}
        className="flex-1 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-lg transition transform hover:scale-105 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-1.5 text-xs font-medium"
      >
        <HelpCircle size={14} /> Not sure
      </button>
    </div>

    <p className="text-center text-xs text-gray-500 pt-1">
      Event {currentIndex + 1} of {totalEvents}
    </p>
  </div>
);

export const EventSwiper = () => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState<{ swipeId?: string; index: number }[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [fetchingEvents, setFetchingEvents] = useState(true);
  const [category, setCategory] = useState<Category | "">("");
  const [locationInput, setLocationInput] = useState(() => {
    if (typeof globalThis.localStorage !== "undefined") {
      return globalThis.localStorage.getItem("taste_location") || "";
    }
    return "";
  });
  const [debouncedLocation, setDebouncedLocation] = useState(locationInput);
  const [isPaired, setIsPaired] = useState<boolean | null>(null);
  const [checkingPairing, setCheckingPairing] = useState(true);
  const user = db.useAuth().user;

  useEffect(() => {
    if (!user || !user.email) {
      setIsPaired(false);
      setCheckingPairing(false);
      return;
    }
    setCheckingPairing(true);
    api({
      endpoint: "checkUserPairing",
      payload: { email: user.email },
    })
      .then((res) => {
        setIsPaired(res.isPaired);
      })
      .catch((err) => {
        console.error("Failed to check pairing status:", err);
        setIsPaired(false);
      })
      .finally(() => {
        setCheckingPairing(false);
      });
  }, [user]);
  const swipesQuery = db.useQuery(
    user
      ? {
        $users: {
          $: {
            where: { id: user.id },
          },
          swipes: {},
        },
      }
      : null,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLocation(locationInput);
    }, 600);
    return () => clearTimeout(timer);
  }, [locationInput]);

  useEffect(() => {
    if (typeof globalThis.localStorage !== "undefined") {
      globalThis.localStorage.setItem("taste_location", locationInput);
    }
  }, [locationInput]);

  useEffect(() => {
    setHistory([]);
    if (debouncedLocation.trim() === "") {
      setEvents([]);
      setFetchingEvents(false);
      return;
    }
    if (user && swipesQuery.isLoading) return;
    setFetchingEvents(true);
    setCurrentIndex(0);
    const swipedEventIds = swipesQuery.data?.$users[0]?.swipes.map(
      ({ eventId }) => eventId,
    ) ?? [];
    api({
      endpoint: "getEvents",
      payload: {
        ...createDefaultQuery(category, debouncedLocation),
        userId: user?.id,
      },
    })
      .then((events) => setEvents(withoutSwipedEvents(events, swipedEventIds)))
      .catch(console.error)
      .finally(() => setFetchingEvents(false));
  }, [user, swipesQuery.isLoading, category, debouncedLocation]);

  const handleSwipe = async (direction: "like" | "dislike") => {
    if (loading) return;
    const currentEvent = events[currentIndex];
    if (!currentEvent) return;

    const swipeId = id();
    setHistory((prev) => [...prev, { swipeId, index: currentIndex }]);
    setLoading(true);
    if (user) {
      try {
        await recordSwipe(user.id, currentEvent, direction, swipeId);
      } catch (err) {
        console.error("Failed to record swipe", err);
      }
    }
    setLoading(false);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleSkip = () => {
    if (loading) return;
    if (currentIndex >= events.length) return;
    setHistory((prev) => [...prev, { index: currentIndex }]);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleUndo = async () => {
    if (loading || history.length === 0) return;
    const lastAction = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setCurrentIndex(lastAction.index);

    if (lastAction.swipeId && user) {
      setLoading(true);
      try {
        await db.transact(db.tx.swipes[lastAction.swipeId].delete());
      } catch (err) {
        console.error("Failed to delete swipe", err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col items-center py-6 px-4 max-w-md mx-auto">
      {/* Filter Controls (always visible) */}
      <div className="w-full mb-6 flex gap-3">
        {/* Category Filter */}
        <div className="flex-1 flex flex-col gap-1.5">
          <label
            htmlFor="category-filter"
            className="text-gray-400 text-xs font-medium"
          >
            Category
          </label>
          <select
            id="category-filter"
            value={category}
            onChange={(e) =>
              setCategory(e.currentTarget.value as Category | "")}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
          >
            <option value="" className="bg-[#1a1a1a]">All Categories</option>
            {Object.entries(cats).map(([id, cat]) => (
              <option key={id} value={id} className="bg-[#1a1a1a]">
                {cat.emoji} {cat.display.en}
              </option>
            ))}
          </select>
        </div>

        {/* Location Filter */}
        <div className="flex-1 flex flex-col gap-1.5">
          <label
            htmlFor="location-filter"
            className="text-gray-400 text-xs font-medium"
          >
            Location
          </label>
          <input
            id="location-filter"
            type="text"
            placeholder="e.g. Tel Aviv"
            value={locationInput}
            onChange={(e) => setLocationInput(e.currentTarget.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent transition"
          />
        </div>
      </div>

      {/* Main Content Area (Dynamic) */}
      {checkingPairing
        ? (
          <div className="w-full h-[580px] flex items-center justify-center">
            <Spinner className="h-8 w-8 text-purple-500" />
          </div>
        )
        : !isPaired
        ? (
          <div className="w-full h-[580px] bg-white/5 border border-white/10 rounded-2xl shadow-2xl flex flex-col items-center justify-center p-6 text-center animate-fade-up">
            <Sparkles className="w-12 h-12 text-purple-400 mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-white mb-2">
              Pair your email first
            </h2>
            <p className="text-gray-400 text-sm mb-6 max-w-sm">
              Please message **Agent FOMO on WhatsApp** to pair your email and
              start training your taste!
            </p>
            <p className="text-purple-300 font-mono font-semibold text-sm mb-6 bg-purple-500/10 border border-purple-500/20 px-4 py-2.5 rounded-lg select-all">
              My email is {user?.email}
            </p>
            <a
              href={`https://wa.me/972504244206?text=My%20email%20is%20${
                encodeURIComponent(user?.email || "")
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-full transition transform hover:scale-105 active:scale-95"
            >
              Pair on WhatsApp
            </a>
          </div>
        )
        : locationInput.trim() === ""
        ? (
          <div className="w-full h-[580px] bg-white/5 border border-white/10 rounded-2xl shadow-2xl flex flex-col items-center justify-center p-6 text-center">
            <h2 className="text-xl font-bold text-white mb-2">
              Enter a location
            </h2>
            <p className="text-gray-400 text-sm">
              Please provide a location above to see events in your area
            </p>
          </div>
        )
        : fetchingEvents
        ? (
          <div className="w-full h-[580px] flex items-center justify-center">
            <Spinner className="h-8 w-8 text-purple-500" />
          </div>
        )
        : currentIndex >= events.length
        ? (
          <div className="w-full h-[580px] bg-white/5 border border-white/10 rounded-2xl shadow-2xl flex flex-col items-center justify-center p-6 text-center">
            <h2 className="text-2xl font-bold text-white mb-2">
              No events found
            </h2>
            <p className="text-gray-400 text-sm mb-6">
              Try adjusting your filters or checking back later
            </p>
            {history.length > 0 && (
              <button
                type="button"
                onClick={handleUndo}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 text-slate-300 font-medium transition disabled:opacity-50 flex items-center gap-2"
              >
                <Undo2 size={16} /> Undo last swipe
              </button>
            )}
          </div>
        )
        : (
          <div className="w-full h-[580px] bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl flex flex-col justify-between overflow-hidden">
            <div className="p-6 flex-1 flex flex-col overflow-y-auto space-y-4">
              <EventDetails event={events[currentIndex]} />
            </div>
            <div className="p-6 pt-0 border-t border-white/5 bg-black/10">
              <SwipeButtons
                loading={loading}
                currentIndex={currentIndex}
                totalEvents={events.length}
                onSwipe={handleSwipe}
                onSkip={handleSkip}
                onUndo={handleUndo}
                canUndo={history.length > 0}
              />
            </div>
          </div>
        )}
    </div>
  );
};
